import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Star, 
  TrendingUp, 
  ChevronRight, 
  Trophy,
  Target,
  BookOpen,
  Award
} from 'lucide-react';
import { ProfileStrengthData } from '@/types/profile';
import { getProfileStrengthLevel } from '@/utils/profile-strength';

interface ProfileStrengthCardProps {
  strengthData: ProfileStrengthData;
  userType: 'student' | 'teacher';
  onEditProfile?: () => void;
  compact?: boolean;
}

const getProgressColorClass = (value: number): string => {
  if (value < 30) return 'bg-red-500';
  if (value < 70) return 'bg-orange-500';
  return 'bg-green-500';
};

export function ProfileStrengthCard({ 
  strengthData, 
  userType, 
  onEditProfile,
  compact = false 
}: ProfileStrengthCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const strengthLevel = getProfileStrengthLevel(strengthData.totalStrength);

  const getCategoryIcon = (categoryKey: string) => {
    switch (categoryKey) {
      case 'basic_info':
        return <User className="w-4 h-4" />;
      case 'academic_info':
      case 'professional_info':
        return <BookOpen className="w-4 h-4" />;
      case 'preferences':
      case 'teaching_profile':
        return <Target className="w-4 h-4" />;
      case 'personal_info':
        return <Star className="w-4 h-4" />;
      case 'additional_info':
        return <Award className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getCategoryTitle = (categoryKey: string) => {
    switch (categoryKey) {
      case 'basic_info':
        return 'Basic Info';
      case 'academic_info':
        return 'Academic Info';
      case 'professional_info':
        return 'Professional Info';
      case 'preferences':
        return 'Preferences';
      case 'teaching_profile':
        return 'Teaching Profile';
      case 'personal_info':
        return 'Personal Info';
      case 'additional_info':
        return 'Additional Info';
      default:
        return categoryKey;
    }
  };

  if (compact) {
    return (
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-primary" />
                </div>
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-sm">Profile Strength</span>
                  <Badge variant="secondary" className={strengthLevel.color}>
                    {Math.floor(strengthData.totalStrength)}%
                  </Badge>
                </div>
                <Progress 
                  value={strengthData.totalStrength} 
                  className="w-32 h-2 mt-1" 
                  indicatorClassName={getProgressColorClass(strengthData.totalStrength)}
                />
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onEditProfile}
              className="text-primary hover:text-primary/80"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Trophy className="w-5 h-5 text-primary" />
            <span>Profile Strength</span>
          </CardTitle>
          <Badge variant="secondary" className={strengthLevel.color}>
            {strengthLevel.title}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Overall Completion</span>
            <span className={`text-lg font-bold ${strengthLevel.color}`}>
              {Math.floor(strengthData.totalStrength)}%
            </span>
          </div>
          <Progress 
            value={strengthData.totalStrength} 
            className="h-3" 
            indicatorClassName={getProgressColorClass(strengthData.totalStrength)}
          />
          <p className="text-xs text-muted-foreground">
            {strengthLevel.description}
          </p>
        </div>

        {/* Category Breakdown */}
        {showDetails && (
          <div className="space-y-3 pt-2 border-t">
            <h4 className="text-sm font-medium flex items-center space-x-2">
              <TrendingUp className="w-4 h-4" />
              <span>Category Breakdown</span>
            </h4>
            
            <div className="space-y-2">
              {Object.entries(strengthData.categories).map(([categoryKey, stats]) => (
                <div key={categoryKey} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getCategoryIcon(categoryKey)}
                      <span className="text-sm">{getCategoryTitle(categoryKey)}</span>
                    </div>
                    <span className="text-sm font-medium">
                      {stats.completed}/{stats.total}
                    </span>
                  </div>
                  <Progress 
                    value={stats.strength} 
                    className="h-1.5" 
                    indicatorClassName={getProgressColorClass(stats.strength)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next Recommended Action */}
        {strengthData.nextRecommendedField && (
          <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start space-x-2">
              <Target className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-primary">
                  Next: Complete {strengthData.nextRecommendedField.label}
                </p>
                {strengthData.nextRecommendedField.description && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {strengthData.nextRecommendedField.description}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-2 pt-2">
          <Button 
            onClick={onEditProfile} 
            className="flex-1"
            size="sm"
          >
            Edit Profile
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Less' : 'More'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
